import RootStore from "./RootStore";
import { InvokeArgs, Signer } from "@waves/signer";
import { ProviderWeb } from "@waves.exchange/provider-web";
import { ProviderCloud } from "@waves.exchange/provider-cloud";
import { ProviderKeeper } from "@waves/provider-keeper";
import {
  CONTRACT_ADDRESSES_MAP,
  EXPLORER_URL_MAP,
  // IPoolConfig,
  // IToken,
  NODE_URL_MAP,
  // POOL_CONFIG,
  POOL_ID,
  ROUTES,
  // TOKENS,
} from "../constants";
import { action, autorun, makeAutoObservable } from "mobx";
// import Balance from "@src/entities/Balance";

import { getCurrentBrowser } from "../utils/getCurrentBrowser";
// import BN from "@src/utils/BN";
import { waitForTx, libs } from "@waves/waves-transactions";

export enum LOGIN_TYPE {
  SIGNER_SEED = "SIGNER_SEED",
  SIGNER_EMAIL = "SIGNER_EMAIL",
  KEEPER = "KEEPER",
}

export interface IInvokeTxParams {
  dApp: string;
  payment: Array<{ assetId: string; amount: string }>;
  call: {
    function: string;
    args: Array<{ type: "integer" | "string"; value: string }>;
  };
}

export interface ITransferTxParams {
  recipient: string;
  attachment?: string;
  assetId?: string;
  amount?: number;
}

export interface ISerializedAccountStore {
  address: string | null;
  loginType: LOGIN_TYPE | null;
}

class AccountStore {
  public readonly rootStore: RootStore;

  chainId: "W" | "T" = "W";

  isWavesKeeperInstalled = false;
  @action.bound setWavesKeeperInstalled = (state: boolean) =>
    (this.isWavesKeeperInstalled = state);

  walletModalOpened: boolean = false;
  @action.bound setWalletModalOpened = (state: boolean) =>
    (this.walletModalOpened = state);

  changePoolModalOpened: boolean = false;
  @action.bound setChangePoolModalOpened = (state: boolean) =>
    (this.changePoolModalOpened = state);

  // public assetBalances: Balance[] = [];
  // @action.bound setAssetBalances = (assetBalances: Balance[]) =>
  //   (this.assetBalances = assetBalances);

  // findBalanceByAssetId = (assetId: string) =>
  //   this.assetBalances.find((balance) => balance.assetId === assetId);

  public address: string | null = null;
  @action.bound setAddress = (address: string | null) =>
    (this.address = address);

  public loginType: LOGIN_TYPE | null = null;
  @action.bound setLoginType = (loginType: LOGIN_TYPE | null) =>
    (this.loginType = loginType);

  public signer: Signer | null = null;
  @action.bound setSigner = (signer: Signer | null) => (this.signer = signer);

  constructor(rootStore: RootStore, initState?: ISerializedAccountStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    if (this.isBrowserSupportsWavesKeeper) {
      this.setupWavesKeeper();
    }
    if (initState) {
      this.setLoginType(initState.loginType);
      if (initState.loginType === LOGIN_TYPE.KEEPER) {
        this.setupSynchronizationWithKeeper();
      }
      this.setAddress(initState.address);
    }
  }

  get isBrowserSupportsWavesKeeper(): boolean {
    const browser = getCurrentBrowser();
    return ["chrome", "firefox", "opera", "edge"].includes(browser);
  }

  setupSynchronizationWithKeeper = () =>
    new Promise((resolve, reject) => {
      let attemptsCount = 0;
      const interval = setInterval(async () => {
        if (window["WavesKeeper"] == null) {
          attemptsCount = attemptsCount + 1;
          if (attemptsCount > 10) {
            clearInterval(interval);
            reject("❌ There is no waves keeper");
          }
        } else {
          clearInterval(interval);
        }

        const result = await window["WavesKeeper"].initialPromise
          .then((keeperApi: any) => keeperApi.publicState())
          .then(() => this.subscribeToKeeperUpdate())
          .catch(({ code }: any) => code === "14" && this.subscribeToKeeperUpdate());
        resolve(result);
      }, 500);
    });

    login = async (loginType: LOGIN_TYPE) => {
      this.setLoginType(loginType);
      switch (loginType) {
        case LOGIN_TYPE.KEEPER:
          this.setSigner(new Signer());
          // const authData = { data: "you know what is the main reason" };
          await this.setupSynchronizationWithKeeper();
          await this.signer?.setProvider(new ProviderKeeper());
          break;
        case LOGIN_TYPE.SIGNER_EMAIL:
          this.setSigner(new Signer());
          await this.signer?.setProvider(new ProviderCloud());
          break;
        case LOGIN_TYPE.SIGNER_SEED:
          this.setSigner(new Signer({ NODE_URL: NODE_URL_MAP[this.chainId] }));
          const provider = new ProviderWeb("https://waves.exchange/signer/");
          await this.signer?.setProvider(provider);
          break;
        default:
          return;
      }
      const loginData = await this.signer?.login();
      this.setAddress(loginData?.address ?? null);
    };

  logout() {
    this.setAddress(null);
    this.setLoginType(null);
  }

  setupWavesKeeper = () => {
    let attemptsCount = 0;

    autorun(
      (reaction) => {
        if (attemptsCount === 2) {
          reaction.dispose();
          // errorMessage({ message: "Waves Keeper is not installed" });
          // alert("keeper is not installed");
        } else if (window['WavesKeeper']) {
          reaction.dispose();
          this.setWavesKeeperInstalled(true);
        } else {
          attemptsCount += 1;
        }
      },
      { scheduler: (run) => setInterval(run, 1000) }
    );
  };

  subscribeToKeeperUpdate = () =>
    window["WavesKeeper"].on("update", (publicState: any) =>
      this.setAddress(publicState.account?.address ?? null)
    );

  serialize = (): ISerializedAccountStore => ({
    address: this.address,
    loginType: this.loginType,
  });

  public invoke = async (txParams: InvokeArgs) =>
    this.loginType === LOGIN_TYPE.KEEPER
      ? this.invokeWithKeeper(txParams)
      : this.invokeWithSigner(txParams);

  public transfer = async (txParams: ITransferTxParams) =>
    this.loginType === LOGIN_TYPE.KEEPER
      ? this.transferWithKeeper(txParams)
      : this.transferWithSigner(txParams);

  private invokeWithSigner = async (
    txParams: InvokeArgs
  ): Promise<string | null> => {
    if (this.signer == null) {
      await this.login(this.loginType ?? LOGIN_TYPE.SIGNER_EMAIL);
    }
    if (this.signer == null) {
      this.rootStore.notificationStore.notify("You need to login first", {
        title: "Error",
        type: "error",
      });
      return null;
    }
    try {
      const ttx = this.signer.invoke({
        dApp: txParams.dApp,
        fee: 500000,
        payment: txParams.payment,
        call: txParams.call,
      });

      return ttx.broadcast().then((tx: any) => tx.id);
    } catch (e: any) {
      console.warn(e);
      this.rootStore.notificationStore.notify(e.toString(), {
        type: "error",
        title: "Transaction is not completed",
      });
      return null;
    }
  };

  private transferWithSigner = async (
    txParams: ITransferTxParams
  ): Promise<string | null> => {
    if (this.signer == null) {
      await this.login(this.loginType ?? LOGIN_TYPE.SIGNER_EMAIL);
    }
    if (this.signer == null) {
      this.rootStore.notificationStore.notify("You need to login first", {
        title: "Error",
        type: "error",
      });
      return null;
    }
    try {
      const ttx = this.signer.transfer({
        recipient: txParams.recipient,
        fee: 100000,
        amount: txParams.amount || 1,
        assetId: txParams.assetId || null,
        attachment: txParams.attachment ? libs.crypto.base58Encode(libs.crypto.stringToBytes(txParams.attachment)) : null
      });

      return ttx.broadcast().then((tx: any) => tx.id);
    } catch (e: any) {
      console.warn(e);
      this.rootStore.notificationStore.notify(e.toString(), {
        type: "error",
        title: "Transaction is not completed",
      });
      return null;
    }
  };

  private invokeWithKeeper = async (
    txParams: InvokeArgs
  ): Promise<string | null> => {
    const data = {
      fee: { assetId: "WAVES", amount: 500000 },
      dApp: txParams.dApp,
      call: txParams.call,
      payment: txParams.payment,
    };
    const tx = await window.WavesKeeper.signAndPublishTransaction({
      type: 16,
      data,
    } as any);

    const txId = JSON.parse(tx).id;
    await waitForTx(txId, {
      apiBase: NODE_URL_MAP[this.chainId],
    });
    return txId;
  };

  private transferWithKeeper = async (
    txParams: ITransferTxParams
  ): Promise<string | null> => {
    const data = {
      amount: { assetId: txParams.assetId || "WAVES", amount: txParams.amount || 1 },
      fee: { assetId: "WAVES", amount: 100000 },
      recipient: txParams.recipient,
      attachment: txParams.attachment || null
    };
    const tx = await window.WavesKeeper.signAndPublishTransaction({
      type: 4,
      data,
    } as any);

    const txId = JSON.parse(tx).id;
    await waitForTx(txId, {
      apiBase: NODE_URL_MAP[this.chainId],
    });
    return txId;
  };

  // get TOKENS() {
  //   return Object.values(TOKENS[this.chainId])
  //     .map((t) => ({
  //       ...t,
  //       logo: tokenLogos[t.symbol] ?? tokenLogos.UNKNOWN,
  //     }))
  //     .reduce(
  //       (acc, token) => ({ ...acc, [token.symbol]: token }),
  //       {} as Record<string, IToken>
  //     );
  // }

  get POOL_ID() {
    return POOL_ID[this.chainId];
  }

  get ROUTES() {
    return ROUTES[this.chainId];
  }

  // get POOL_CONFIG(): Record<string, IPoolConfig> {
  //   return POOL_CONFIG[this.chainId];
  // }

  get EXPLORER_LINK() {
    return EXPLORER_URL_MAP[this.chainId];
  }

  get CONTRACT_ADDRESSES() {
    return CONTRACT_ADDRESSES_MAP[this.chainId];
  }
}

export default AccountStore;
